import React, {
  useEffect, useState, useContext
} from 'react';
import {
  Button
} from 'shared/components/ui/button';
import {
  FaCheck, FaCopy, FaWhatsapp
} from 'react-icons/fa';
import {
  useParams
} from 'react-router-dom';
import {
  Context
} from 'shared/context/GlobalState';
import {
  channelService, baileysBridgeService, redisCollectionService, coexistenceService
} from 'shared/api';
import {
  ChannelSetupLayout
} from '../ChannelSetupLayout';
import type {
  Channel, RedisChannelAdd
} from '../../types/builderChannel.types';
import WhatsAppConfigSidebar from './WhatsAppConfigSidebar';
import BaileysConnectionPanel from './BaileysConnectionPanel';

interface WhatsAppSetupProps {
  setOpenEmailModal?: (open: boolean) => void;
  setEmailChannels?: React.Dispatch<React.SetStateAction<Channel[]>>;
  newChannelObj?: RedisChannelAdd | Channel;
  setNewChannelObj?: React.Dispatch<React.SetStateAction<RedisChannelAdd | Channel>>;
  isUpdate?: boolean;
  onSubmit?: (channel: RedisChannelAdd | Channel) => Promise<void>;
  onBack?: () => void;
}

/**
 * A fresh 32-byte signing secret for one co-existence number.
 *
 * Registrations at the wamcp001 gateway are PER NUMBER, each with its own secret the
 * gateway HMAC-signs relayed callbacks with, and FEO checks X-MCP-Signature-256
 * against the copy stored on the channel. Generated with the Web Crypto API rather
 * than Math.random: this value is the only thing authenticating inbound traffic for
 * this number.
 */
const generateWebhookSecret = (): string => {
  const bytes = new Uint8Array(32);
  window.crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
};

const WhatsAppSetup: React.FC<WhatsAppSetupProps> = ({
  setOpenEmailModal,
  setEmailChannels,
  newChannelObj,
  setNewChannelObj,
  isUpdate = false,
  onSubmit,
  onBack,
}) => {

  const {
    oragID, subOragID
  } = useParams<{ oragID: string; subOragID: string }>();
  // Org-scoped callback URL. FEO rejects the org-less form with a 400 (#427, and
  // #407 before it): without :organizationId the channel lookup runs with no tenant
  // filter and can resolve another org’s — or another environment’s — channel.
  const FEO_CALLBACK_URL = `${process.env.REACT_APP_FEO_SERVER_URL}/feo-v3/api/whatsapp/Receive/${oragID}`;
  // Co-existence callback carries NO org: Meta allows one webhook per Facebook App and
  // the app is shared across every hosted number, so tenancy rides on the phone number
  // instead (the display number resolves to exactly one channel).
  const COEXISTENCE_CALLBACK_URL = `${process.env.REACT_APP_FEO_SERVER_URL}/feo-v3/api/whatsapp/coexistence/webhook`;
  const {
    addNewNotifcation
  } = useContext(Context);

  const [channelData, setChannelData] = useState({
    type: 'whatsapp',
    address: 'whatsapp:+',
    topic: '',
    assistantId: !isUpdate && newChannelObj?.assistant_id ? String(newChannelObj.assistant_id) : '',
  });

  const currentChannelData = isUpdate ? newChannelObj : channelData;
  const currentSetChannelData = isUpdate ? setNewChannelObj : setChannelData;

  const [copied, setCopied] = useState(false);

  const [provider, setProvider] = useState<'twilio' | 'baileys' | 'coexistence'>(() => {
    if (isUpdate && newChannelObj) {
      // Trust the stored `provider` (required on create since S-0005). The old
      // address regex mis-read anything not shaped like `whatsapp:+…` as Baileys,
      // which is exactly what an API/MCP-created channel looks like. The regex is
      // kept only for pre-schema records that carry no provider at all.
      const stored = ((newChannelObj as Channel).provider || '').toLowerCase();
      if (stored === 'twilio' || stored === 'baileys' || stored === 'coexistence') return stored;
      return /^whatsapp:\+\d/.test(newChannelObj.address || '') ? 'twilio' : 'baileys';
    }
    return 'twilio';
  });

  // Co-existence: the Meta Cloud API Phone Number ID. Distinct from `address` (the
  // display number) — see the sidebar field copy for why conflating them is fatal.
  const [phoneNumberId, setPhoneNumberId] = useState<string>(() =>
    (isUpdate && newChannelObj ? (newChannelObj as Channel).phone_number_id ?? '' : ''));

  // Outcome of the gateway callback registration. The channel is created first and
  // registered second (registration reads the secret off the record), so the wizard
  // has to be able to report "created but not receiving" as its own state rather than
  // leaving an inert channel behind with a success toast.
  const [registerState, setRegisterState] = useState<
    { status: 'idle' | 'pending' } | { status: 'ok'; url: string } | { status: 'failed'; error: string }
  >({ status: 'idle' });

  const [receiveEnabled, setReceiveEnabled] = useState<boolean>(() => {
    if (isUpdate && newChannelObj && 'receive_enabled' in newChannelObj) {
      return (newChannelObj as Channel).receive_enabled ?? true;
    }
    return true;
  });

  const [sendMode, setSendMode] = useState<'auto' | 'tool' | 'disabled'>(() => {
    if (isUpdate && newChannelObj && 'send_mode' in newChannelObj) {
      return (newChannelObj as Channel).send_mode ?? 'auto';
    }
    return 'auto';
  });

  // Group Monitor (Use case A): pair the number as a receive-only monitor. Messages are
  // harvested to the channel's KB collection; the assistant is never invoked and nothing
  // is sent back. Scoped to Baileys + QR (the backend forwards mode only on the QR path).
  const [monitorMode, setMonitorMode] = useState<boolean>(() => {
    if (isUpdate && newChannelObj && 'send_mode' in newChannelObj) {
      const c = newChannelObj as Channel;
      return c.send_mode === 'disabled' && (c.receive_enabled ?? true);
    }
    return false;
  });

  const [qrCode, setQrCode] = useState<string>('');
  const [pairingCode, setPairingCode] = useState<string>('');
  const [connectionMethod, setConnectionMethod] = useState<'qr' | 'phone'>('qr');
  const [phoneForPairing, setPhoneForPairing] = useState<string>('');
  const [channelCreated, setChannelCreated] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState<string>('');

  // A monitor never replies, so its send_mode is always "disabled" regardless of the
  // send-mode selector (which is hidden while monitoring). It also only supports the QR
  // connection method — the mode is forwarded to the Bridge on the QR path only.
  const effectiveSendMode: 'auto' | 'tool' | 'disabled' = monitorMode ? 'disabled' : sendMode;
  useEffect(() => {
    if (monitorMode) setConnectionMethod('qr');
  }, [monitorMode]);

  // A monitor harvests into a KB collection (it has no opcode to own KB routing, so the
  // channel holds the binding). Load the sub-org's collections when monitoring is on.
  const [collectionId, setCollectionId] = useState<string>(
    isUpdate && (newChannelObj as any)?.collectionId ? String((newChannelObj as any).collectionId) : ''
  );
  const [collections, setCollections] = useState<Array<{ _id: string; name: string }>>([]);

  // Listener source filter: which inbound to harvest. "selected" = the specific groups
  // and/or people picked below (JIDs live in selectedSources / receive_sources).
  const [receiveFilter, setReceiveFilter] = useState<'all' | 'groups' | 'individuals' | 'selected_groups' | 'selected'>(() => {
    if (isUpdate && newChannelObj && (newChannelObj as any).receive_filter) {
      const f = (newChannelObj as any).receive_filter;
      // Legacy "selected_groups" edits as "selected": the sources of a legacy record
      // are group JIDs only, and "selected" matches exactly those same JIDs — so the
      // effective behavior is unchanged while the picker UI stays a single mode.
      return f === 'selected_groups' ? 'selected' : f;
    }
    return 'all';
  });
  // For "selected": the groups + individual contacts the connected session knows about,
  // and the user's picks (a mixed set of group and contact JIDs → receive_sources).
  const [availableGroups, setAvailableGroups] = useState<Array<{ id: string; name: string; participants?: number }>>([]);
  const [availableContacts, setAvailableContacts] = useState<Array<{ id: string; name: string; phoneNumber?: string }>>([]);
  const [selectedSources, setSelectedSources] = useState<string[]>(
    isUpdate && Array.isArray((newChannelObj as any)?.receive_sources) ? (newChannelObj as any).receive_sources : []
  );
  const [groupsLoading, setGroupsLoading] = useState(false);
  // Free-text filter for the group/people picker.
  const [pickerQuery, setPickerQuery] = useState('');
  useEffect(() => {
    if (!monitorMode || !oragID || !subOragID) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await redisCollectionService.listCollections(oragID, subOragID);
        const list = (res.data ?? res) as Array<{ _id: string; name: string }>;
        if (!cancelled) setCollections(Array.isArray(list) ? list : []);
      } catch {
        if (!cancelled) setCollections([]);
      }
    })();
    return () => { cancelled = true; };
  }, [monitorMode, oragID, subOragID]);

  const [baileysSessionAddress] = useState<string>(() => {
    if (isUpdate && newChannelObj && 'address' in newChannelObj) {
      return newChannelObj.address as string;
    }
    return crypto.randomUUID();
  });

  const handleChange = (value: string, type: keyof typeof channelData) => {
    if (isUpdate && currentSetChannelData) {
      currentSetChannelData((prev: any) => ({
        ...prev,
        [type]: value
      }));
    } else {
      setChannelData((prev) => ({
        ...prev,
        [type]: value
      }));
    }
  };

  // Retry just the registration half. The channel already exists, so this never
  // re-creates it — it re-points the gateway at our webhook for that number
  // (registration is replace-on-register, so retrying is safe to repeat).
  const retryCoexistenceRegistration = async () => {
    const address = (currentChannelData as any)?.address || channelData.address;
    setRegisterState({ status: 'pending' });
    try {
      const regRes = await coexistenceService.registerCallback(oragID || '', {
        address,
        callback_url: COEXISTENCE_CALLBACK_URL,
      });
      const registeredUrl = regRes?.data?.data?.callback_url || COEXISTENCE_CALLBACK_URL;
      setRegisterState({ status: 'ok', url: registeredUrl });
      addNewNotifcation('Callback registered — the channel is now receiving.', 'success');
      setOpenEmailModal?.(false);
    } catch (err: any) {
      const detail = err?.response?.data?.error
        || err?.response?.data?.message
        || err?.message
        || 'Unknown error';
      setRegisterState({ status: 'failed', error: String(detail) });
      addNewNotifcation('Callback registration failed again.', 'danger');
    }
  };

  const handleNumberChange = (value: string) => {
    // Twilio addresses are `whatsapp:+<E164>`; co-existence stores BARE DIGITS,
    // because inbound routing strips Meta's display_phone_number to digits and
    // matches on that. A prefixed co-existence address matches nothing, forever.
    const address = provider === 'coexistence'
      ? value.replace(/[^0-9]/g, '')
      : `whatsapp:+${value}`;
    if (isUpdate && currentSetChannelData) {
      currentSetChannelData((prev: any) => ({
        ...prev,
        address
      }));
    } else {
      setChannelData((prev) => ({
        ...prev,
        address
      }));
    }
  };

  // Takes the URL rather than closing over the Twilio one: the co-existence panel
  // shows a different callback, and without this it had no way to be copied at all.
  const handleCopyClick = (url: string = FEO_CALLBACK_URL) => {
    navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const isButtonDisabled = () => {
    const data = currentChannelData || channelData;
    const assistantId = (data as any).assistantId ?? (data as any).assistant_id;
    // A group monitor never invokes an assistant, so one is not required for it.
    const assistantOk = monitorMode ? true : !!assistantId;
    // A listener must harvest into a KB collection — creating (or saving) one without
    // a collection leaves its messages unsearchable, so the binding is required. When
    // editing a "choose specific chats" listener, an empty pick would silently harvest
    // nothing, so at least one source is required too (creation picks sources in a
    // separate post-connect step, gated by its own Save button).
    const listenerOk = !monitorMode
      || (!!collectionId && !(isUpdate && receiveFilter === 'selected' && selectedSources.length === 0));
    if (provider === 'baileys') {
      if (connectionMethod === 'phone' && !isUpdate) {
        return channelCreated || !data.topic?.trim() || !assistantOk || !listenerOk || !phoneForPairing.trim();
      }
      return channelCreated || !data.topic?.trim() || !assistantOk || !listenerOk;
    }
    // Co-existence needs BOTH ids, and they must not be the same value — the swap is
    // the one mistake nothing downstream can catch (both are plain digits).
    if (provider === 'coexistence') {
      const addressDigits = (data.address || '').replace(/[^0-9]/g, '');
      return (
        !data.topic?.trim()
        || !addressDigits
        || !phoneNumberId.trim()
        || phoneNumberId.trim() === addressDigits
        || !assistantOk
      );
    }
    return (
      !data.topic?.trim() ||
      !data.address?.trim() ||
      data.address?.trim() === 'whatsapp:+' ||
      !assistantId
    );
  };

  const [saveError, setSaveError] = useState<string>('');

  // Fetch the groups AND known individual contacts of the (now connected) session — both
  // feed the "selected" picker. Groups come from a complete API; contacts are best-effort
  // (history sync + whoever has DMed the number), so that list can start sparse and grow.
  const fetchGroups = async (quiet = false) => {
    if (!quiet) setGroupsLoading(true);
    try {
      const [gRes, cRes] = await Promise.all([
        baileysBridgeService.getGroups(oragID || '', subOragID || '', baileysSessionAddress).catch(() => null),
        baileysBridgeService.getContacts(oragID || '', subOragID || '', baileysSessionAddress).catch(() => null),
      ]);
      const groups = gRes?.data?.data?.groups ?? gRes?.data?.groups ?? [];
      const contacts = cRes?.data?.data?.contacts ?? cRes?.data?.contacts ?? [];
      // Alphabetical by name so the picker is scannable (the API returns them unordered).
      const byName = (a: { name?: string }, b: { name?: string }) =>
        (a.name || '').localeCompare(b.name || '');
      setAvailableGroups(Array.isArray(groups) ? [...groups].sort(byName) : []);
      setAvailableContacts(Array.isArray(contacts) ? [...contacts].sort(byName) : []);
    } catch {
      if (!quiet) {
        setAvailableGroups([]);
        setAvailableContacts([]);
      }
    } finally {
      if (!quiet) setGroupsLoading(false);
    }
  };

  // The actual save. Runs immediately on connect for most filters; for "selected groups"
  // it runs after the user has picked groups (via the Save button in the picker).
  const finalizeSave = async () => {
    const dataToSave = currentChannelData || channelData;
    setConnectionStatus('saving');
    try {
      const saveRes = await baileysBridgeService.saveChannel(oragID || '', {
        subOrganizationId: subOragID || '',
        topic: dataToSave.topic || '',
        address: baileysSessionAddress,
        assistantId: (dataToSave as any).assistantId ?? (dataToSave as any).assistant_id ?? '',
        provider: 'baileys',
        receive_enabled: receiveEnabled,
        send_mode: effectiveSendMode,
        ...(monitorMode && collectionId ? { collectionId } : {}),
        ...(monitorMode ? { receive_filter: receiveFilter } : {}),
        ...(monitorMode && (receiveFilter === 'selected' || receiveFilter === 'selected_groups')
          ? { receive_sources: selectedSources }
          : {}),
      });
      if (setEmailChannels && saveRes.data?.data) {
        setEmailChannels((prev) => [...prev, saveRes.data.data]);
      }
      setConnectionStatus('connected');
      addNewNotifcation('WhatsApp instance connected successfully!', 'success');
      setTimeout(() => setOpenEmailModal?.(false), 1500);
    } catch (saveErr: any) {
      const status = saveErr.response?.status;
      const msg = status === 403
        ? (saveErr?.response?.data?.message || 'Resource limit. You can not create more channels under your current plan')
        : status === 409
          ? 'Channel already exists'
          : 'Failed to save channel. Please try again.';
      setSaveError(msg);
      setConnectionStatus('save_failed');
      baileysBridgeService.disconnect(oragID || '', subOragID || '', baileysSessionAddress).catch(() => {});
    }
  };

  useEffect(() => {
    if (!(channelCreated && (connectionStatus === 'pending' || connectionStatus === 'pairing') && baileysSessionAddress)) {
      return;
    }

    const startedAt = Date.now();
    const MAX_WAIT_MS = 150000; // a QR/pairing link that never completes must not poll forever
    // 'disconnected' must persist across several polls before it counts as terminal: the
    // bridge passes through it transiently mid-link (session init before the first QR,
    // QR refresh re-initialize), and a single 1s poll landing in such a window used to
    // abort the wizard while the bridge went on to link successfully.
    const DISCONNECTED_STREAK_TO_FAIL = 3;
    let disconnectedStreak = 0;
    let interval: any;

    const fail = (msg: string) => {
      clearInterval(interval);
      setSaveError(msg);
      setConnectionStatus('save_failed');
      baileysBridgeService.disconnect(oragID || '', subOragID || '', baileysSessionAddress).catch(() => {});
    };

    interval = setInterval(async () => {
      // Give up on a link that never completes instead of spinning indefinitely.
      if (Date.now() - startedAt > MAX_WAIT_MS) {
        fail('Connection timed out. Please close this dialog and try again.');
        return;
      }
      try {
        const statusRes = await baileysBridgeService.checkStatus(oragID || '', subOragID || '', baileysSessionAddress);
        const polledQr = statusRes.data?.data?.qr;
        const polledStatus = statusRes.data?.data?.status;
        if (polledQr) setQrCode(polledQr);

        // Terminal backend failure states — surface them instead of ignoring and polling
        // forever (the old bug: only 'connected' was ever acted on). 'reauth_required' is
        // always deliberate on the bridge side and fails immediately; 'disconnected' only
        // fails after a sustained streak (see DISCONNECTED_STREAK_TO_FAIL above).
        if (polledStatus === 'reauth_required') {
          fail('The session expired before it finished linking. Please try again.');
          return;
        }
        if (polledStatus === 'disconnected') {
          disconnectedStreak += 1;
          if (disconnectedStreak >= DISCONNECTED_STREAK_TO_FAIL) {
            fail('The connection dropped before it finished linking. Please try again.');
          }
          return;
        }
        disconnectedStreak = 0;

        if (polledStatus === 'connected') {
          clearInterval(interval);
          // The capture choice is made up front. Only "choose specific chats" needs a
          // second step — the group/people list exists only once connected. Every other
          // choice (and conversational channels) saves immediately via finalizeSave.
          if (monitorMode && receiveFilter === 'selected') {
            setConnectionStatus('select_groups');
            fetchGroups();
          } else {
            await finalizeSave();
          }
        }
      } catch (error) {
        // A transient poll error (bridge/proxy blip) is NOT a link failure — keep polling
        // until the overall timeout above decides to give up.
        console.error('Polling error', error);
      }
    }, 1000);

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channelCreated, connectionStatus, baileysSessionAddress, oragID, subOragID, monitorMode, receiveFilter]);

  // WhatsApp streams contacts to the phone gradually after linking (there is no way to
  // pull the address book on demand — see the note in the picker). While the picker is
  // open, quietly re-fetch so people appear as they sync, without a jarring reload.
  useEffect(() => {
    if (connectionStatus !== 'select_groups') return;
    const t = setInterval(() => { fetchGroups(true); }, 5000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connectionStatus]);

  // In update mode the picker is inline (there is no post-connect step), so load the
  // session's groups/contacts as soon as a "selected" listener is being edited.
  useEffect(() => {
    if (isUpdate && monitorMode && receiveFilter === 'selected') fetchGroups();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUpdate, monitorMode, receiveFilter]);

  const handleOnSubmit = async () => {
    if (isUpdate && onSubmit) {
      // Listener bindings live in local state (not in currentChannelData, which only
      // tracks the sidebar fields) — merge them in so the update payload carries them.
      const listenerFields = monitorMode
        ? {
            collectionId,
            receive_filter: receiveFilter,
            ...(receiveFilter === 'selected' ? { receive_sources: selectedSources } : {}),
          }
        : {};
      await onSubmit({ ...(currentChannelData as RedisChannelAdd | Channel), ...listenerFields });
      return;
    }

    // Co-existence pre-flight. Both values are numeric, so a swap passes every
    // downstream schema and produces a channel that looks healthy and is deaf —
    // catch it here where the user can still see which field is which.
    if (provider === 'coexistence') {
      const addressDigits = (channelData.address || '').replace(/[^0-9]/g, '');
      if (!addressDigits) {
        addNewNotifcation('Enter the WhatsApp Business number.', 'danger');
        return;
      }
      if (!phoneNumberId) {
        addNewNotifcation('Enter the Meta Phone Number ID — without it, replies would go out from another number.', 'danger');
        return;
      }
      if (phoneNumberId === addressDigits) {
        addNewNotifcation('The Meta Phone Number ID cannot be the phone number itself. Check the Meta app dashboard.', 'danger');
        return;
      }
    }

    const payload = {
      org_id: oragID || '',
      sub_org_id: subOragID || '',
      address: provider === 'baileys'
        ? baileysSessionAddress
        : channelData.address,
      topic: channelData.topic,
      type: channelData.type,
      provider,
      assistant_id: channelData.assistantId || '',
      receive_enabled: receiveEnabled,
      send_mode: effectiveSendMode,
      // Co-existence: the Meta id, plus a freshly generated per-number signing secret.
      // The secret is generated here and never shown — FEO reads it back off the
      // channel when it registers the callback, so it does not travel a second time.
      ...(provider === 'coexistence'
        ? {
            phone_number_id: phoneNumberId,
            webhook_secret: generateWebhookSecret(),
          }
        : {}),
    };

    if (provider === 'baileys') {
      if (connectionMethod === 'phone') {
        try {
          const res = await baileysBridgeService.getPairingCode(oragID || '', subOragID || '', baileysSessionAddress, phoneForPairing.replace(/[^0-9]/g, ''));
          const code = res.data?.data?.code;
          if (code) setPairingCode(code);
          setConnectionStatus('pairing');
          setChannelCreated(true);
          addNewNotifcation('Enter the code in WhatsApp → Linked Devices → Link with phone number.', 'success');
        } catch (err) {
          console.error('Pairing code error', err);
          addNewNotifcation('Failed to get pairing code. Is the Baileys bridge running?', 'danger');
        }
        return;
      }

      try {
        // Optimistic: show QR panel immediately while Baileys initializes
        setConnectionStatus('pending');
        const initRes = await baileysBridgeService.generateQr(oragID || '', subOragID || '', baileysSessionAddress, monitorMode ? 'listener' : undefined);
        const initialQr = initRes.data?.data?.qr;
        if (initialQr) setQrCode(initialQr);
        setChannelCreated(true);
      } catch (qrErr) {
        console.error('QR Generate Error', qrErr);
        addNewNotifcation('Failed to generate QR code. Is the Baileys bridge running?', 'danger');
        return;
      }

      addNewNotifcation('Scan the QR code to connect your WhatsApp account.', 'success');
      return;
    }

    try {
      const res = await channelService.addChannel(payload);

      // Co-existence has a second half: until the gateway callback is registered,
      // Meta has nowhere to deliver and the channel receives nothing. Registration
      // needs the record to exist (FEO reads the secret and Meta id off it), so it
      // cannot gate creation — but it must not be silent either. Report it.
      if (provider === 'coexistence') {
        setRegisterState({ status: 'pending' });
        try {
          const regRes = await coexistenceService.registerCallback(oragID || '', {
            address: payload.address,
            callback_url: COEXISTENCE_CALLBACK_URL,
          });
          const registeredUrl = regRes?.data?.data?.callback_url || COEXISTENCE_CALLBACK_URL;
          setRegisterState({ status: 'ok', url: registeredUrl });
          addNewNotifcation('Channel created and callback registered.', 'success');
          setOpenEmailModal?.(false);
          setEmailChannels?.((prev) => [res.data, ...prev]);
        } catch (regErr: any) {
          // Keep the dialog open. The channel exists and is editable, but it is not
          // receiving, and saying "added successfully" here is how inert channels get
          // handed over as working ones.
          const detail = regErr?.response?.data?.error
            || regErr?.response?.data?.message
            || regErr?.message
            || 'Unknown error';
          setRegisterState({ status: 'failed', error: String(detail) });
          setEmailChannels?.((prev) => [res.data, ...prev]);
          addNewNotifcation('Channel created, but it is NOT receiving — callback registration failed.', 'danger');
        }
        return;
      }

      addNewNotifcation('Channel added successfully', 'success');
      setOpenEmailModal?.(false);
      setEmailChannels?.((prev) => [res.data, ...prev]);
    } catch (error: any) {
      if (error.response?.status === 409) {
        addNewNotifcation('Channel already exists', 'danger');
      } else if (error.response?.status === 403) {
        addNewNotifcation(
          error?.response?.data?.message ||
            'Resource limit. You can not create more channels under your current plan',
          'danger'
        );
      } else {
        addNewNotifcation('Something went wrong', 'danger');
      }
    }
  };

  const data = currentChannelData || channelData;

  // Picker filtering: match groups/people by name (and contacts by number too).
  const pickerQ = pickerQuery.trim().toLowerCase();
  const filteredGroups = pickerQ
    ? availableGroups.filter((g) => (g.name || '').toLowerCase().includes(pickerQ))
    : availableGroups;
  const filteredContacts = pickerQ
    ? availableContacts.filter((c) =>
        (c.name || '').toLowerCase().includes(pickerQ) || (c.phoneNumber || '').includes(pickerQ))
    : availableContacts;

  // Capture settings (source filter + KB collection) — shared between the create
  // flow's purpose step and the update dialog's listener panel.
  const listenerCaptureSettings = (
      <>
          <div className="space-y-1.5">
              <span className="text-[11px] font-semibold text-muted-foreground block">
                  What should it collect? <span className="text-destructive">*</span>
              </span>
              <div className="space-y-1.5">
                  {[
                      { value: 'all', title: 'Everything', desc: 'Every group and every direct message.' },
                      { value: 'groups', title: 'Groups only', desc: 'Only group chats. Direct messages are ignored.' },
                      { value: 'individuals', title: 'Direct messages only', desc: 'Only direct chats. Groups are ignored.' },
                      { value: 'selected', title: 'Choose specific chats', desc: isUpdate ? 'Pick exact groups and people below.' : 'Pick exact groups and people after connecting.' },
                  ].map((opt) => {
                      const active = receiveFilter === opt.value;
                      return (
                          <button
                              key={opt.value}
                              type="button"
                              onClick={() => setReceiveFilter(opt.value as any)}
                              className={`w-full flex items-start gap-2.5 rounded-lg border p-2.5 text-left transition ${active ? 'border-primary bg-primary/5 ring-1 ring-primary/30' : 'border-border hover:border-muted-foreground/40'}`}
                          >
                              <span className={`mt-0.5 w-3.5 h-3.5 rounded-full border-2 shrink-0 flex items-center justify-center ${active ? 'border-primary' : 'border-muted-foreground/40'}`}>
                                  {active && <span className="w-1.5 h-1.5 rounded-full bg-primary" />}
                              </span>
                              <span className="min-w-0">
                                  <span className="block text-xs font-semibold text-foreground">{opt.title}</span>
                                  <span className="block text-[10px] text-muted-foreground leading-snug">{opt.desc}</span>
                              </span>
                          </button>
                      );
                  })}
              </div>
          </div>
          <div className="space-y-1.5">
              <span className="text-[11px] font-semibold text-muted-foreground block">
                  Save to knowledge collection <span className="text-destructive">*</span>
              </span>
              <select
                  value={collectionId}
                  onChange={(e) => setCollectionId(e.target.value)}
                  className="w-full rounded-lg border border-border bg-background px-3 py-2 text-xs text-foreground"
              >
                  <option value="">Select a collection…</option>
                  {collections.map((c) => (
                      <option key={c._id} value={c._id}>{c.name}</option>
                  ))}
              </select>
              <p className="text-[10px] text-muted-foreground leading-snug">
                  Send several listeners to the <strong>same</strong> collection to build one shared knowledge base, or give each its own to keep them separate.
              </p>
          </div>
      </>
  );

  // Group/people checklist — shared between the post-connect picker (create) and the
  // update dialog's inline picker.
  const sourceList = (
      <>
          <input
              type="text"
              value={pickerQuery}
              onChange={(e) => setPickerQuery(e.target.value)}
              placeholder="Search groups and people"
              className="w-full rounded-lg border border-border bg-background px-3 py-2 text-xs text-foreground placeholder:text-muted-foreground"
          />
          <div className="max-h-[280px] overflow-y-auto rounded-lg border border-border">
              {filteredGroups.length === 0 && filteredContacts.length === 0 && (
                  <p className="text-xs text-muted-foreground px-3 py-4 text-center">No groups or people match “{pickerQuery}”.</p>
              )}
              {filteredGroups.length > 0 && (
                  <div className="flex items-center justify-between px-3 py-1.5 bg-muted text-[10px] font-semibold uppercase tracking-wider text-muted-foreground sticky top-0 z-10 border-y border-border">
                      <span>Groups</span>
                      <span className="tabular-nums">{filteredGroups.length}</span>
                  </div>
              )}
              {filteredGroups.map((g) => {
                  const checked = selectedSources.includes(g.id);
                  return (
                      <label key={g.id} className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-muted/50 border-b border-border/50">
                          <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => setSelectedSources((prev) => checked ? prev.filter((x) => x !== g.id) : [...prev, g.id])}
                          />
                          <span className="flex-1 min-w-0">
                              <span className="block text-xs font-medium text-foreground truncate">{g.name}</span>
                              {typeof g.participants === 'number' && (
                                  <span className="block text-[10px] text-muted-foreground">{g.participants} members</span>
                              )}
                          </span>
                      </label>
                  );
              })}
              {filteredContacts.length > 0 && (
                  <div className="flex items-center justify-between px-3 py-1.5 bg-muted text-[10px] font-semibold uppercase tracking-wider text-muted-foreground sticky top-0 z-10 border-y border-border">
                      <span>People</span>
                      <span className="tabular-nums">{filteredContacts.length}</span>
                  </div>
              )}
              {filteredContacts.map((c) => {
                  const checked = selectedSources.includes(c.id);
                  return (
                      <label key={c.id} className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-muted/50 border-b border-border/50">
                          <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => setSelectedSources((prev) => checked ? prev.filter((x) => x !== c.id) : [...prev, c.id])}
                          />
                          <span className="flex-1 min-w-0">
                              <span className="block text-xs font-medium text-foreground truncate">{c.name || c.phoneNumber || c.id}</span>
                              {!!c.name && c.phoneNumber && (
                                  <span className="block text-[10px] text-muted-foreground">{c.phoneNumber}</span>
                              )}
                          </span>
                      </label>
                  );
              })}
          </div>
          {availableContacts.length === 0 && availableGroups.length > 0 && (
              <p className="text-[10px] text-muted-foreground leading-snug">
                  Only groups are listed so far. WhatsApp hasn't synced contacts yet. People appear here as they sync, or the moment they message this number.
              </p>
          )}
      </>
  );

  return (
      <ChannelSetupLayout
      onCancel={() => setOpenEmailModal?.(false)}
      onSubmit={handleOnSubmit}
      submitDisabled={isButtonDisabled()}
      onBack={onBack}
      title="Configure WhatsApp Instance"
      isDashboard={true}
      fixedHeight={true}
    >
          <div className="flex flex-col sm:flex-row w-full h-full min-h-0">
              <WhatsAppConfigSidebar
          provider={provider}
          setProvider={setProvider}
          topic={data.topic ?? ''}
          assistantId={(data as any).assistantId ?? (data as any).assistant_id ?? ''}
          address={data.address ?? ''}
          hideAssistant={!!(newChannelObj as any)?.isAssistantDisabled || monitorMode}
          receiveEnabled={receiveEnabled}
          sendMode={effectiveSendMode}
          onTopicChange={(value) => handleChange(value, 'topic')}
          onAssistantChange={(value) => handleChange(value, 'assistantId')}
          onNumberChange={handleNumberChange}
          onReceiveEnabledChange={(value) => {
            setReceiveEnabled(value);
            if (isUpdate && currentSetChannelData) {
              currentSetChannelData((prev: any) => ({ ...prev, receive_enabled: value }));
            }
          }}
          onSendModeChange={(value) => {
            setSendMode(value);
            if (isUpdate && currentSetChannelData) {
              currentSetChannelData((prev: any) => ({ ...prev, send_mode: value }));
            }
          }}
          connectionMethod={connectionMethod}
          setConnectionMethod={setConnectionMethod}
          phoneForPairing={phoneForPairing}
          onPhoneForPairingChange={setPhoneForPairing}
          isUpdate={isUpdate}
          isListener={monitorMode}
          phoneNumberId={phoneNumberId}
          onPhoneNumberIdChange={(value) => {
            setPhoneNumberId(value);
            if (isUpdate && currentSetChannelData) {
              currentSetChannelData((prev: any) => ({ ...prev, phone_number_id: value }));
            }
          }}
        />

              <main className="flex-grow bg-background p-8 flex flex-col items-center justify-center relative overflow-y-auto min-h-0">
                  <div className="w-full max-w-sm space-y-8 animate-in fade-in slide-in-from-bottom-2 duration-500">
                      {provider === 'twilio' ? (
                          <div className="space-y-6 text-center">
                              <div className="w-16 h-16 bg-blue-500/10 text-blue-500 rounded-2xl flex items-center justify-center mx-auto">
                                  <FaWhatsapp size={32} />
                              </div>
                              <div className="space-y-2">
                                  <h4 className="text-lg font-bold text-foreground">Twilio Webhook</h4>
                                  <p className="text-sm text-muted-foreground">Configure your Twilio console with the URL below to receive inbound messages.</p>
                              </div>
                              <div className="flex items-center gap-2 p-1.5 bg-muted border border-border rounded-lg">
                                  <code className="flex-grow text-[11px] text-muted-foreground font-mono px-2 overflow-hidden text-ellipsis whitespace-nowrap">
                                      {FEO_CALLBACK_URL}
                                  </code>
                                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => handleCopyClick(FEO_CALLBACK_URL)}
                    className="h-8 w-8"
                  >
                                      {copied ? <FaCheck className="text-green-500 size-3" /> : <FaCopy className="text-muted-foreground size-3" />}
                                  </Button>
                              </div>
                          </div>
            ) : provider === 'coexistence' ? (
                <div className="space-y-6 text-center">
                    <div className="w-16 h-16 bg-emerald-500/10 text-emerald-600 rounded-2xl flex items-center justify-center mx-auto">
                        <FaWhatsapp size={32} />
                    </div>
                    <div className="space-y-2">
                        <h4 className="text-lg font-bold text-foreground">Meta Cloud API (Co-existence)</h4>
                        <p className="text-sm text-muted-foreground">
                            The number stays live in the WhatsApp Business app and also runs on Meta's
                            Cloud API. Saving registers this callback with the gateway for you.
                        </p>
                    </div>
                    {/* Wraps rather than ellipsises, and has a copy button. This URL is
                        registered with Meta and is the one value that silently kills the
                        channel if it is wrong — truncating it to "…coexistence/we…" with no
                        way to copy it made the one thing the user must verify unreadable. */}
                    <div className="flex items-start gap-2 p-1.5 bg-muted border border-border rounded-lg text-left">
                        <code className="flex-grow text-[11px] text-muted-foreground font-mono px-2 py-1 break-all select-all">
                            {COEXISTENCE_CALLBACK_URL}
                        </code>
                        <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            onClick={() => handleCopyClick(COEXISTENCE_CALLBACK_URL)}
                            className="h-8 w-8 shrink-0"
                            aria-label="Copy callback URL"
                        >
                            {copied ? <FaCheck className="text-green-500 size-3" /> : <FaCopy className="text-muted-foreground size-3" />}
                        </Button>
                    </div>

                    {registerState.status === 'pending' && (
                        <p className="text-xs text-muted-foreground">Registering the callback with the gateway…</p>
                    )}

                    {registerState.status === 'ok' && (
                        <div className="rounded-lg border border-emerald-500/40 bg-emerald-500/5 p-3 text-left">
                            <p className="text-xs font-semibold text-emerald-600">Callback registered</p>
                            <div className="flex items-start gap-2 mt-1">
                                <p className="text-[11px] text-muted-foreground break-all select-all flex-grow">
                                    Meta will deliver to {registerState.url}
                                </p>
                                <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => handleCopyClick(registerState.url)}
                                    className="h-6 w-6 shrink-0"
                                    aria-label="Copy registered URL"
                                >
                                    {copied ? <FaCheck className="text-green-500 size-3" /> : <FaCopy className="text-muted-foreground size-3" />}
                                </Button>
                            </div>
                        </div>
                    )}

                    {registerState.status === 'failed' && (
                        <div className="rounded-lg border border-destructive/40 bg-destructive/5 p-3 text-left space-y-2">
                            <p className="text-xs font-semibold text-destructive">
                                Channel created, but it is NOT receiving
                            </p>
                            <p className="text-[11px] text-muted-foreground leading-snug">
                                The gateway callback was not registered, so Meta has nowhere to deliver
                                messages for this number. The channel exists and can be retried.
                            </p>
                            <p className="text-[11px] text-muted-foreground font-mono break-all">
                                {registerState.error}
                            </p>
                            <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={retryCoexistenceRegistration}
                                className="w-full"
                            >
                                Retry registration
                            </Button>
                        </div>
                    )}

                    <p className="text-[10px] text-muted-foreground italic leading-snug">
                        Sending also needs this number's WhatsApp Business Account assigned to the
                        shared app's Meta system user. That is done in Meta Business Manager, not here.
                    </p>
                </div>
            ) : (
                <div className="text-center flex flex-col items-center gap-6">
                    {!isUpdate && connectionStatus === '' && (
                        <div className="w-full text-left space-y-4">
                            <div className="space-y-1.5">
                                <span className="text-[11px] font-semibold text-muted-foreground block">
                                    What is this channel for? <span className="text-destructive">*</span>
                                </span>
                                <div className="grid grid-cols-2 gap-2">
                                    <button
                                        type="button"
                                        onClick={() => setMonitorMode(false)}
                                        className={`rounded-lg border p-3 text-left transition ${!monitorMode ? 'border-primary bg-primary/5 ring-1 ring-primary/30' : 'border-border hover:border-muted-foreground/40'}`}
                                    >
                                        <span className="block text-xs font-semibold text-foreground">Conversation</span>
                                        <span className="block text-[10px] text-muted-foreground mt-0.5 leading-snug">A normal chat. The assistant reads and replies.</span>
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setMonitorMode(true)}
                                        className={`rounded-lg border p-3 text-left transition ${monitorMode ? 'border-primary bg-primary/5 ring-1 ring-primary/30' : 'border-border hover:border-muted-foreground/40'}`}
                                    >
                                        <span className="block text-xs font-semibold text-foreground">Listener</span>
                                        <span className="block text-[10px] text-muted-foreground mt-0.5 leading-snug">Collects messages into knowledge. Never replies.</span>
                                    </button>
                                </div>
                            </div>
                            {monitorMode && (
                                <>
                                    {listenerCaptureSettings}
                                    <p className="text-[10px] text-muted-foreground leading-snug border-t border-border/60 pt-2.5">
                                        Connect with a phone that belongs to the groups or people you want to collect from. It only listens. It never sends or replies.
                                    </p>
                                </>
                            )}
                        </div>
                    )}
                    {connectionStatus === 'select_groups' ? (
                        <div className="w-full text-left space-y-3">
                            <div className="flex items-start justify-between gap-2">
                                <div>
                                    <h4 className="text-sm font-semibold text-foreground">Connected. Pick the chats to collect</h4>
                                    <p className="text-[11px] text-muted-foreground">Only the groups and people you tick will be collected. Nothing is saved until you confirm below.</p>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => fetchGroups()}
                                    disabled={groupsLoading}
                                    className="shrink-0 text-[11px] font-medium text-primary hover:underline disabled:opacity-50"
                                >
                                    {groupsLoading ? 'Refreshing' : 'Refresh'}
                                </button>
                            </div>
                            {groupsLoading ? (
                                <div className="flex items-center gap-2 text-xs text-muted-foreground py-6 justify-center">
                                    <span className="inline-block w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" /> Loading groups and people…
                                </div>
                            ) : (availableGroups.length === 0 && availableContacts.length === 0) ? (
                                <div className="space-y-3 py-2">
                                    <p className="text-xs text-muted-foreground leading-snug">
                                        There's nothing to pick yet. This number isn't in any groups, and no contacts have synced.
                                        WhatsApp fills contacts in gradually, and anyone who messages this number appears here automatically.
                                    </p>
                                    <Button type="button" variant="outline" onClick={() => { setReceiveFilter('all'); finalizeSave(); }} className="w-full">
                                        Collect everything for now
                                    </Button>
                                </div>
                            ) : (
                                <>
                                    {sourceList}
                                    <Button
                                        type="button"
                                        onClick={() => finalizeSave()}
                                        disabled={selectedSources.length === 0}
                                        className="w-full"
                                    >
                                        {selectedSources.length === 0
                                            ? 'Tick at least one chat'
                                            : `Save listener · ${selectedSources.length} selected`}
                                    </Button>
                                </>
                            )}
                        </div>
                    ) : isUpdate && monitorMode ? (
                        <div className="w-full text-left space-y-4">
                            <div>
                                <h4 className="text-sm font-semibold text-foreground">Listener settings</h4>
                                <p className="text-[11px] text-muted-foreground">
                                    Change what this listener collects and which knowledge collection it saves into. Changes apply from the next received message — already collected messages stay where they are.
                                </p>
                            </div>
                            {listenerCaptureSettings}
                            {receiveFilter === 'selected' && (
                                <div className="space-y-3">
                                    <div className="flex items-center justify-between">
                                        <span className="text-[11px] font-semibold text-muted-foreground">
                                            Chats to collect <span className="text-destructive">*</span>
                                        </span>
                                        <button
                                            type="button"
                                            onClick={() => fetchGroups()}
                                            disabled={groupsLoading}
                                            className="shrink-0 text-[11px] font-medium text-primary hover:underline disabled:opacity-50"
                                        >
                                            {groupsLoading ? 'Refreshing' : 'Refresh'}
                                        </button>
                                    </div>
                                    {groupsLoading && availableGroups.length === 0 && availableContacts.length === 0 ? (
                                        <div className="flex items-center gap-2 text-xs text-muted-foreground py-6 justify-center">
                                            <span className="inline-block w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" /> Loading groups and people…
                                        </div>
                                    ) : (availableGroups.length === 0 && availableContacts.length === 0) ? (
                                        <p className="text-xs text-muted-foreground leading-snug">
                                            Nothing to pick yet. If the phone is connected, groups appear right away and contacts sync in gradually — try Refresh, or choose a different filter above.
                                        </p>
                                    ) : (
                                        sourceList
                                    )}
                                </div>
                            )}
                        </div>
                    ) : (
                        <BaileysConnectionPanel
                  connectionStatus={connectionStatus}
                  qrCode={qrCode}
                  saveError={saveError}
                  hideAssistant={!!(newChannelObj as any)?.isAssistantDisabled || monitorMode}
                  pairingCode={pairingCode}
                />
                    )}
                </div>
            )}
                  </div>
              </main>
          </div>
      </ChannelSetupLayout>
  );
};

export default WhatsAppSetup;
